import { supabase } from '../lib/supabase';
import { MeetingInstance, MeetingType, AttendanceData, CreateMeetingFormData } from '../types/database';
import { generateInstanceId } from '../lib/utils';

export class MeetingService {
  // Get convener's assigned meeting types
  static async getConvenorMeetingTypes(userId: string): Promise<{ data: MeetingType[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('user_meeting_assignments')
        .select(`
          meeting_types (
            id,
            name,
            level
          )
        `)
        .eq('user_id', userId);

      if (error) throw error;

      const meetingTypes = data?.flatMap(item => item.meeting_types).filter(Boolean) as MeetingType[] || [];
      return { data: meetingTypes, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Create new meeting instance
  static async createInstance(
    userId: string,
    meetingTypeId: string,
    formData: CreateMeetingFormData
  ): Promise<{ data: MeetingInstance | null; error: Error | null }> {
    try {
      // Get meeting type details for instance ID generation
      const { data: meetingType } = await supabase
        .from('meeting_types')
        .select('name, level')
        .eq('id', meetingTypeId)
        .single();

      if (!meetingType) {
        throw new Error('Meeting type not found');
      }

      const instanceId = generateInstanceId(meetingType.name, meetingType.level, formData.date);

      const { data, error } = await supabase
        .from('meeting_instances')
        .insert({
          instance_id: instanceId,
          meeting_type_id: meetingTypeId,
          date: formData.date,
          time: formData.time || null,
          created_by: userId,
          status: 'Scheduled'
        })
        .select()
        .single();

      if (error) throw error;

      return { data: data as MeetingInstance, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get convener's meeting instances
  static async getMyInstances(userId: string): Promise<{ data: MeetingInstance[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('meeting_instances')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          ),
          attendance_records (
            id,
            status
          )
        `)
        .eq('created_by', userId)
        .order('date', { ascending: false });

      if (error) throw error;

      // Calculate attendance summaries
      const instancesWithSummary = (data as any[]).map(instance => {
        const attendanceRecords = instance.attendance_records || [];
        const total = attendanceRecords.length;
        const present = attendanceRecords.filter((record: any) => record.status === 'Present').length;
        const absent = total - present;
        const percentage = total > 0 ? Math.round((present / total) * 100) : 0;

        return {
          ...instance,
          attendance_summary: {
            total,
            present,
            absent,
            percentage
          }
        };
      });

      return { data: instancesWithSummary as MeetingInstance[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get members for a meeting type
  static async getMembersForMeetingType(meetingTypeId: string): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .eq('meeting_type_id', meetingTypeId)
        .order('name');

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Mark attendance for a meeting instance
  static async markAttendance(
    instanceId: string,
    attendanceData: AttendanceData[],
    markedByUserId: string
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      const attendanceRecords = attendanceData.map(record => ({
        meeting_instance_id: instanceId,
        member_id: record.member_id,
        status: record.status,
        marked_by: markedByUserId,
        marked_at: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('attendance_records')
        .upsert(attendanceRecords, { onConflict: 'meeting_instance_id,member_id' })
        .select();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get attendance for a meeting instance
  static async getAttendanceForInstance(instanceId: string): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('attendance_records')
        .select(`
          *,
          members (
            id,
            name,
            phone
          )
        `)
        .eq('meeting_instance_id', instanceId);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Update meeting instance
  static async updateInstance(
    instanceId: string,
    updates: Partial<MeetingInstance>
  ): Promise<{ data: MeetingInstance | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('meeting_instances')
        .update(updates)
        .eq('id', instanceId)
        .select()
        .single();

      if (error) throw error;

      return { data: data as MeetingInstance, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Delete meeting instance
  static async deleteInstance(instanceId: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('meeting_instances')
        .delete()
        .eq('id', instanceId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  // Get convener stats & meaningful insights
  static async getConvenorStats(userId: string): Promise<{ data: any | null; error: Error | null }> {
    try {
      // 1. Get assigned meeting types
      const { data: meetingTypes } = await this.getConvenorMeetingTypes(userId);
      const meetingTypeIds = meetingTypes?.map(mt => mt.id) || [];

      // 2. Get all meeting instances for this convener
      const { data: instances } = await supabase
        .from('meeting_instances')
        .select(`
          id,
          status,
          date,
          meeting_type_id,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .eq('created_by', userId)
        .order('date', { ascending: false });

      const totalMeetings = instances?.length || 0;
      const completedMeetings = instances?.filter(m => m.status === 'Completed').length || 0;
      const scheduledMeetings = instances?.filter(m => m.status === 'Scheduled').length || 0;

      const meetingIds = instances?.map(m => m.id) || [];

      // 3. Get all members for assigned meeting types
      let members: any[] = [];
      if (meetingTypeIds.length > 0) {
        const { data: mData } = await supabase
          .from('members')
          .select(`
            id,
            name,
            phone,
            meeting_type_id,
            meeting_types (
              id,
              name,
              level
            )
          `)
          .in('meeting_type_id', meetingTypeIds);
        members = mData || [];
      }

      // 4. Get all attendance records for convener's meetings
      let attendanceRecords: any[] = [];
      if (meetingIds.length > 0) {
        const { data: aData } = await supabase
          .from('attendance_records')
          .select('id, meeting_instance_id, member_id, status')
          .in('meeting_instance_id', meetingIds);
        attendanceRecords = aData || [];
      }

      const totalAttendanceMarked = attendanceRecords.length;
      const totalPresent = attendanceRecords.filter(r => r.status === 'Present').length;
      const totalAbsent = attendanceRecords.filter(r => r.status === 'Absent').length;

      const overallAttendanceRate = totalAttendanceMarked > 0
        ? Math.round((totalPresent / totalAttendanceMarked) * 100)
        : 0;

      // 5. Per-meeting-type stats breakdown
      const typeStatsMap: Record<string, { meetingsCount: number; membersCount: number; presentCount: number; totalRecords: number }> = {};

      meetingTypes?.forEach(mt => {
        typeStatsMap[mt.id] = {
          meetingsCount: 0,
          membersCount: members.filter(m => m.meeting_type_id === mt.id).length,
          presentCount: 0,
          totalRecords: 0
        };
      });

      const instanceTypeMap: Record<string, string> = {};
      instances?.forEach(inst => {
        instanceTypeMap[inst.id] = inst.meeting_type_id;
        if (typeStatsMap[inst.meeting_type_id]) {
          typeStatsMap[inst.meeting_type_id].meetingsCount++;
        }
      });

      attendanceRecords.forEach(rec => {
        const typeId = instanceTypeMap[rec.meeting_instance_id];
        if (typeId && typeStatsMap[typeId]) {
          typeStatsMap[typeId].totalRecords++;
          if (rec.status === 'Present') {
            typeStatsMap[typeId].presentCount++;
          }
        }
      });

      const meetingTypeBreakdown = meetingTypes?.map(mt => {
        const tStat = typeStatsMap[mt.id] || { meetingsCount: 0, membersCount: 0, presentCount: 0, totalRecords: 0 };
        const rate = tStat.totalRecords > 0 ? Math.round((tStat.presentCount / tStat.totalRecords) * 100) : 0;
        return {
          id: mt.id,
          name: mt.name,
          level: mt.level,
          meetingsCount: tStat.meetingsCount,
          membersCount: tStat.membersCount,
          attendanceRate: rate
        };
      }) || [];

      // 6. Member health analysis
      const memberMeetingsMap: Record<string, { present: number; total: number }> = {};
      attendanceRecords.forEach(rec => {
        if (!memberMeetingsMap[rec.member_id]) {
          memberMeetingsMap[rec.member_id] = { present: 0, total: 0 };
        }
        memberMeetingsMap[rec.member_id].total++;
        if (rec.status === 'Present') {
          memberMeetingsMap[rec.member_id].present++;
        }
      });

      let highAttendanceCount = 0;
      let atRiskCount = 0;
      let zeroAttendanceCount = 0;

      const memberSummaries = members.map(m => {
        const mTypeMeetings = instances?.filter(i => i.meeting_type_id === m.meeting_type_id).length || 0;
        const mStats = memberMeetingsMap[m.id] || { present: 0, total: 0 };
        const totalEligible = mTypeMeetings;
        const present = mStats.present;
        const rate = totalEligible > 0 ? Math.round((present / totalEligible) * 100) : 0;

        if (totalEligible > 0) {
          if (rate >= 80) highAttendanceCount++;
          else if (rate < 50) atRiskCount++;
          if (present === 0) zeroAttendanceCount++;
        }

        return {
          ...m,
          totalEligible,
          present,
          rate
        };
      });

      const atRiskMembers = memberSummaries
        .filter(m => m.totalEligible > 0 && m.rate < 60)
        .sort((a, b) => a.rate - b.rate)
        .slice(0, 5);

      const stats = {
        totalMeetings,
        completedMeetings,
        scheduledMeetings,
        totalMembers: members.length,
        attendanceMarked: totalAttendanceMarked,
        totalPresent,
        totalAbsent,
        overallAttendanceRate,
        completionRate: totalMeetings > 0 ? Math.round((completedMeetings / totalMeetings) * 100) : 0,
        highAttendanceCount,
        atRiskCount,
        zeroAttendanceCount,
        meetingTypeBreakdown,
        atRiskMembers
      };

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get attendance for a specific meeting
  static async getAttendanceForMeeting(meetingInstanceId: string): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('attendance_records')
        .select(`
          id,
          member_id,
          meeting_instance_id,
          status,
          members (
            id,
            name,
            phone
          )
        `)
        .eq('meeting_instance_id', meetingInstanceId);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Update attendance record
  static async updateAttendance(
    meetingInstanceId: string,
    memberId: string,
    status: 'Present' | 'Absent'
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('attendance_records')
        .upsert(
          {
            meeting_instance_id: meetingInstanceId,
            member_id: memberId,
            status,
            marked_at: new Date().toISOString()
          },
          { onConflict: 'meeting_instance_id,member_id' }
        )
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }
}
